import { useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../../shared/utils/tableUtils"
import { SelectChangeEvent, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { faUserGroup, faLocationDot, faArrowsRotate } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../../shared/components/table/TableSummaryBox"
import {
    fetchUserResourceBooking,
    FetchUserResourceBookingResponse,
    UpcomingOrdersResourceBooking,
} from "../userResourceBookingApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { ResourceBooking } from "../userResourceBookingTypes"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { selectUserId } from "../../../../../login/loginSelectors"
import { DatePeriod } from "../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { MeetingCardMenu } from "../../../home/components/upcomingOrders/meetings/MeetingCardMenu"
import { MenuMeeting } from "../../../home/weekMenuTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ResourceTypeFilter } from "../components/ResourceTypeFilter"
import { ResourceType } from "../../../../../serviceCenter/admin/organization/resources/resourcesTypes"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"

const useStyles = makeStyles()(() => ({
    wrapIconText: {
        marginRight: ".5rem",
        color: tailwindPalette.slate[500],
    },
}))

const selectUpcomingOrders = (data: FetchUserResourceBookingResponse) => data?.orders || []

export function UserResourceBooking() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)
    const dateTimeFactory = useDateTime()
    const todayStart = dateTimeFactory.now().startOf("day")

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [selectedResources, setSelectedResources] = useState<ResourceType[]>([])
    const [datePeriod, setDatePeriod] = useState<DatePeriod>(DatePeriod.ALL)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate < todayStart ? todayStart : startDate,
        endDate: endDate && endDate < todayStart ? todayStart : endDate,
    })
    const tableInstanceRef = useRef<MRT_TableInstance<ResourceBooking>>(null)

    const upcomingOrdersResult = useApiQuery(fetchUserResourceBooking, {
        queryName: UpcomingOrdersResourceBooking.UPCOMING_ORDERS_RESOURCE_BOOKING,
        dependencies: {
            userId: userId ?? 0,
            resourceTypes: selectedResources.length ? selectedResources.join(",") : undefined,
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate?.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate?.toISODate() : undefined,
        },
        select: selectUpcomingOrders,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(upcomingOrdersResult)

    const columns = useMemo<MRT_ColumnDef<ResourceBooking>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeCompactPeriod(
                {
                    header: l10n.getString("user-upcoming-orders-resource-booking-table-time"),
                    accessorKey: "meetingDetails.startDateTime",
                },
                (row) => row.meetingDetails.startDateTime,
                (row) => row.meetingDetails.endDateTime,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-meeting"),
                accessorKey: "meetingDetails",
                Cell: ({ row }) => {
                    return (
                        <StandardTwoLinesCell
                            title={row.original.meetingDetails.meetingName}
                            subtitle1={
                                <>
                                    <FontAwesomeIcon icon={faLocationDot} className={classes.wrapIconText} />
                                    {row.original.meetingDetails.location}
                                </>
                            }
                            subtitle2={
                                <>
                                    <FontAwesomeIcon icon={faUserGroup} className={classes.wrapIconText} />
                                    {row.original.orderSummary.participants}
                                </>
                            }
                            subtitle3={
                                row.original.meetingDetails?.recurrence ? (
                                    <FontAwesomeIcon icon={faArrowsRotate} className={classes.wrapIconText} />
                                ) : undefined
                            }
                        />
                    )
                },
            }),
            columnFactory.statusColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-visibility"),
                id: "visibility",
                accessorFn: (row) => (row.meetingDetails.isPrivate ? "yes" : "no"),
                Cell: ({ row }) => {
                    return (
                        <Tooltip
                            arrow
                            title={
                                row.original.meetingDetails.isPrivate
                                    ? l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-private-tooltip",
                                      )
                                    : l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-public-tooltip",
                                      )
                            }
                        >
                            <div>
                                {!row.original.meetingDetails.isPrivate ? (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-public",
                                        )}
                                        variant="success"
                                        placement="table"
                                    />
                                ) : (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-private",
                                        )}
                                        variant="red"
                                        placement="table"
                                    />
                                )}
                            </div>
                        </Tooltip>
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const handleResourceTypeChange = (event: SelectChangeEvent<typeof selectedResources>) => {
        const {
            target: { value },
        } = event
        const selected = typeof value === "string" ? value.split(",") : value

        if (selected.length === selectedResources.length) {
            setSelectedResources([])
        } else {
            setSelectedResources(selected as ResourceType[])
        }
    }

    const mapResourceBookingToMeeting = (booking: ResourceBooking): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            orderAction: booking.orderAction,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MeetingCardMenu
                        meetings={
                            getFilteredTableData(tableInstanceRef)
                        }
                        ignoreMenuContainer
                        meeting={mapResourceBookingToMeeting(row.original)}
                        menuOnApi={false}
                        handleMenuClose={closeMenu}
                    />,
                ]}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <TimeOrDatePicker
                            isDatePicker={isDatePicker}
                            dateState={dateState}
                            setDateState={setDateState}
                            timePeriod={datePeriod}
                            handleChangeTimePeriod={setDatePeriod}
                            handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                        />
                        <ResourceTypeFilter value={selectedResources} onChange={handleResourceTypeChange} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={upcomingOrdersResult.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>
                    </TableSummaryWrap>
                )}
            />
        </>
    )
}
